import {yupResolver} from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import {Box, Button, CircularProgress, Grid, TextField} from '@mui/material';
import axios from 'axios';
import React, {useEffect, useRef, useState} from 'react';
import {FormProvider, useForm, useWatch} from 'react-hook-form';
import ReactMarkdown from 'react-markdown';
import {useSelector} from 'react-redux';
import {i18n, i18nExists} from 'src/i18n';
import authSelectors from 'src/modules/auth/authSelectors';
import OpenAIService from 'src/modules/shared/openai/openAIService';
import yupFormSchemas from 'src/modules/shared/yup/yupFormSchemas';
import Storage from 'src/security/storage';
import CategoriaAutocompleteFormItem from 'src/view/categoria/autocomplete/CategoriaAutocompleteFormItem';
import ModeloPrincipalFormularioForm from 'src/view/modeloFormulario/form/ModeloPrincipalFormularioForm';
import ModeloTareaForm from 'src/view/modeloTarea/form/ModeloTareaForm';
import ImagesFormItem from 'src/view/shared/form/items/ImagesFormItem';
import InputFormItem from 'src/view/shared/form/items/InputFormItem';
import SelectFormItem from 'src/view/shared/form/items/SelectFormItem';
import SwitchFormItem from 'src/view/shared/form/items/SwitchFormItem';
import Message from 'src/view/shared/message';
import ConfirmModal from 'src/view/shared/modals/ConfirmModal';
import FormWrapper, {FormButtons} from 'src/view/shared/styles/FormWrapper';
import * as yup from 'yup';
import modeloFormularioEnumerators from '../../../modules/modeloFormulario/modeloFormularioEnumerators';

import Resizer from 'react-image-file-resizer';
import incidentEnumerators from 'src/modules/incident/incidentEnumerators';
import {
  applyWireQuestionFieldsToDomain,
  domainAnswerTypeToWire,
  domainOptionsToWire,
  domainToWireAnswerTypeForExport,
  getWireAnswerTypesForClasificacion,
  incidentKeyPointerFromTipo,
  pickDomainQuestionFieldsForWire,
  wireAnswerTypeToDomain,
  wireOptionsToAlternativas,
} from 'src/modules/modeloFormulario/modeloFormularioAiSchema';
import perfilEmpleadoEnumerators from 'src/modules/perfilEmpleado/perfilEmpleadoEnumerators';
import {normalizeIsTaskConfig} from 'src/modules/tarea/isTaskConfig';
import DivisionAutocompleteFormItem from 'src/view/division/autocomplete/DivisionAutocompleteFormItem';
import {
  applyDivisionIdPolicyToPayload,
  canShowDivisionIdFieldInForm,
  mapRecordOrCurrentDivisionToFormDivisionId,
} from 'src/view/shared/division/divisionIdFormPolicy';
import CheckboxFormItem from 'src/view/shared/form/items/CheckboxFormItem';
import {ConditionalRulesTable} from './ConditionalRulesTable';

/** Pasar a false para ofrecer las preguntas del parte Blumar en todos los tenants. */
const hideBlumarStuffForOthers = true;
const BLUMAR_SSO_TENANT_ID = '62a8b6dd2323505723b7e92b';
const BLUMAR_INCIDENT_ANSWER_TYPES = new Set([
  'incident_contactPhone',
  'incident_directManagerPosition',
  'incident_propertyDamage',
  'incident_workPerformed',
  'incident_atSea',
  'incident_causingAgent',
  'incident_injuryType',
  'incident_narratorName',
  'incident_narratorRole',
  'incident_causalEffect',
  'incident_causalProblem',
  'incident_causalCause1',
  'incident_causalCause2',
  'incident_causalCause3',
  'incident_investigationConclusion',
  'incident_incidentWeekday',
  'incident_accionesSubestandar',
  'incident_condicionesSubestandar',
  'incident_tipoAccidente',
]);

const schema = yup.object().shape({
  adjuntarFirmaEjecutor: yupFormSchemas.boolean('Adjuntar firma ejecutor', {}),
  clasificacion: yupFormSchemas.enumerator(i18n('entities.tablaCostoEnergia.fields.clasificacion'), {
    options: modeloFormularioEnumerators.clasificacion,
  }),
  nombre: yupFormSchemas.string(i18n('entities.modeloFormulario.fields.nombre'), {
    required: true,
  }),
  sucursales: yupFormSchemas.relationToMany(i18n('entities.modeloFormulario.fields.sucursales'), {
    required: false,
  }),
  formato: yupFormSchemas.generic(i18n('entities.modeloFormulario.fields.formato')),
  categoriaGeneral: yupFormSchemas.relationToOne(i18n('entities.modeloFormulario.fields.categoriaGeneral'), {
    required: true,
  }),
  categoriasFormulario: yupFormSchemas.relationToMany(i18n('entities.modeloFormulario.fields.categoriasFormulario'), {}),
  divisionId: yupFormSchemas.relationToOne(i18n('entities.division.label'), {required: false}),
  habilidades: yupFormSchemas.relationToMany(i18n('entities.modeloFormulario.fields.habilidades'), {
    required: false,
  }),
  crearTarea: yupFormSchemas.boolean('Crear Tarea', {}),
  tareaData: yupFormSchemas.generic('Datos Tarea'),
  conditionalRules: yupFormSchemas.generic('Acciones Condicionales'),
});

/** Keys de pregunta compatibles con ConditionalRulesTable / listaProcedimiento (`subsectionIndex_nPregunta`). */
const collectQuestionKeysFromFormato = (formato: any[] = []): Set<string> => {
  const keys = new Set<string>();
  const formatoSafe = Array.isArray(formato) ? formato : [];
  let listIndex = 0;

  for (const seccion of formatoSafe) {
    listIndex++;
    const subsecciones = Array.isArray(seccion?.subseccions) ? seccion.subseccions : [];

    for (const subseccion of subsecciones) {
      const subsectionListIndex = listIndex;
      listIndex++;
      const preguntas = Array.isArray(subseccion?.preguntas) ? subseccion.preguntas : [];

      for (const p of preguntas) {
        const nPregunta = p?.nPregunta;
        if (typeof nPregunta === 'number' || typeof nPregunta === 'string') {
          keys.add(`${subsectionListIndex}_${String(nPregunta)}`);
        }
        listIndex++;
      }
    }
  }

  return keys;
};

const normalizeRuleCombinator = (value: any, fallback: 'AND' | 'OR'): 'AND' | 'OR' => {
  const s = String(value ?? '').toUpperCase();
  return s === 'AND' || s === 'OR' ? s : fallback;
};

const normalizeConditionLeaf = (leaf: any = {}) => ({
  questionKey: leaf?.questionKey != null ? String(leaf.questionKey) : '',
  matchMode: String(leaf?.matchMode ?? 'CONTAINS').toUpperCase() || 'CONTAINS',
  matchText: leaf?.matchText != null ? String(leaf.matchText) : '',
});

const normalizeRuleConditionGroups = (rule: any = {}) => {
  const groupsSource = Array.isArray(rule?.groups) && rule.groups.length ? rule.groups : null;

  const groups = groupsSource
    ? groupsSource.map((group: any) => ({
        combinator: normalizeRuleCombinator(group?.combinator, 'AND'),
        conditions:
          Array.isArray(group?.conditions) && group.conditions.length
            ? group.conditions.map(normalizeConditionLeaf)
            : [normalizeConditionLeaf()],
      }))
    : [
        {
          combinator: 'AND',
          conditions: [normalizeConditionLeaf(rule)],
        },
      ];

  const firstLeaf = groups[0]?.conditions?.[0] || normalizeConditionLeaf();

  return {
    groupCombinator: normalizeRuleCombinator(rule?.groupCombinator, 'OR'),
    groups,
    questionKey: firstLeaf.questionKey,
    matchMode: firstLeaf.matchMode,
    matchText: firstLeaf.matchText,
  };
};

const sanitizeConditionalRules = (rules: any[] = [], formato?: any[]) => {
  const pickIdLabel = (v: any) => {
    if (!v) return null;

    // si viene como string (id)
    if (typeof v === 'string') return {id: v, label: v};

    // si viene como objeto
    const id = v?.id ?? v?._id ?? v?.value ?? null;

    // label típico en tus autocompletes
    const label = v?.label ?? v?.fullName ?? v?.name ?? v?.titulo ?? v?.title ?? v?.email ?? (id ? String(id) : '');

    if (!id) return null;
    return {id, label};
  };

  const normalizeRuleMulti = (v: any): string[] => {
    if (!v) return [];
    if (Array.isArray(v)) return v.filter(Boolean).map(String);
    if (typeof v === 'string')
      return v
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    return [];
  };

  const validQuestionKeys = formato !== undefined ? collectQuestionKeysFromFormato(formato) : null;

  return rules.map((rule) => {
    const recipients = Array.isArray(rule.recipients) ? rule.recipients : [];
    const ruleActions = normalizeRuleMulti(rule?.actions);
    const raSet = new Set(ruleActions);

    let emailHighlightQuestionKeys = normalizeRuleMulti(rule?.emailHighlightQuestionKeys);
    if (validQuestionKeys) {
      emailHighlightQuestionKeys = emailHighlightQuestionKeys.filter((k) => validQuestionKeys.has(k));
    }

    const conditionShape = normalizeRuleConditionGroups(rule);

    const sanitizeRecipientRow = (r: any, keepActions: boolean) => {
      let out: any = {...r};

      if (r?.mode === 'USER') {
        out.value = pickIdLabel(r?.value);
      } else if (r?.mode === 'LOCATION_RESPONSIBLE' || r?.mode === 'TEAM') {
        out.value = pickIdLabel(r?.value);
      } else if (r?.mode === 'EXECUTOR') {
        out.value = undefined;
      }

      if (!keepActions) {
        delete out.actions;
        return out;
      }

      let rActions = normalizeRuleMulti(out.actions);
      if (rActions.length) {
        rActions = rActions.filter((a) => raSet.has(String(a)));
      }
      if (rActions.length) {
        out.actions = rActions;
      } else {
        delete out.actions;
      }

      return out;
    };

    const validatorRecipients = Array.isArray(rule.validatorRecipients)
      ? rule.validatorRecipients
      : [];

    return {
      ...rule,
      ...conditionShape,
      emailHighlightQuestionKeys,
      attachTaskPdf: rule?.attachTaskPdf !== false,
      emailSeparateRecipients: rule?.emailSeparateRecipients === true,

      // ✅ modeloTarea queda como {id, label} o null
      modeloTarea: pickIdLabel(rule?.modeloTarea),

      recipients: recipients.map((r: any) => sanitizeRecipientRow(r, true)),
      validatorRecipients: validatorRecipients.map((r: any) => sanitizeRecipientRow(r, false)),
    };
  });
};

function ModeloFormularioForm(props) {
  const [valid, setValid] = useState<any>(false);
  const [initialValues] = useState(() => {
    const record = props.record || {};

    const formatoDefault = record.formato?.length
      ? record.formato
      : [
          {
            categoria: '',
            isDynamic: false,
            allowNotApplicable: false,
            subseccions: [{categoria: '', isDynamic: false, allowNotApplicable: false, subTableKind: ''}],
          },
        ];
    return {
      nombre: record.nombre,
      clasificacion: record.clasificacion || 'operativa',
      adjuntarFirmaEjecutor: record.adjuntarFirmaEjecutor || false,
      sucursales: record.sucursales || [],
      categoriaGeneral: record.categoriaGeneral,
      categoriasFormulario: record.categoriasFormulario || [],
      divisionId: mapRecordOrCurrentDivisionToFormDivisionId(record),
      habilidades: record.habilidades || [],
      formato: formatoDefault,
      crearTarea: false,
      tareaData: {},
      conditionalRules: sanitizeConditionalRules(record.conditionalRules || [], formatoDefault),
    };
  });

  const form = useForm({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: initialValues as any,
    shouldUnregister: false,
  });

  const userRoles = useSelector(authSelectors.selectRoles);
  const currentTenant = useSelector(authSelectors.selectCurrentTenant);
  const tenantAccessContext = useSelector(authSelectors.selectTenantAccessContext);
  const showBlumarIncidentFields =
    !hideBlumarStuffForOthers || currentTenant?.id === BLUMAR_SSO_TENANT_ID;
  const hasAccessToIA = userRoles.includes('accesoIA');
  const isTenantAdmin = userRoles.includes('admin');
  const showDivisionIdsField = canShowDivisionIdFieldInForm(tenantAccessContext, isTenantAdmin) && !props.disabled;

  useEffect(() => {
    pushHistory({
      messagesToShow: messages,
      localFormat: {formato: initialValues.formato, conditionalRules: initialValues.conditionalRules || []},
    });
  }, []);

  const mapIncidentEnum = (name, options) => {
    return options.map((option) => ({
      id: option,
      label: i18n(`entities.incident.enumerators.${name}.${option}`),
    }));
  };

  const mapPerfilEmpleadoEnum = (nameKey, options) => {
    return options.map((option) => {
      const path = `entities.perfilEmpleado.enumerators.${nameKey}.${option}`;
      return {
        id: option,
        label: i18nExists(path) ? i18n(path) : option,
      };
    });
  };

  const mapIncidentAlternativeTreePath = (options) => {
    const SEP = '\\';

    const buildTree = (obj, path = '') => {
      return Object.keys(obj).map((key) => {
        const currentPath = path ? `${path}${SEP}${key}` : key;
        const value = currentPath;
        const label = key;

        if (Array.isArray(obj[key])) {
          return {
            value,
            label,
            children: obj[key].map((subValue) => {
              const leafPath = `${currentPath}${SEP}${subValue}`;
              return {
                parent: currentPath,
                value: leafPath,
                label: subValue,
              };
            }),
          };
        }

        return {
          value,
          label,
          children: buildTree(obj[key], currentPath),
        };
      });
    };

    return buildTree(options);
  };

  const mapIncidentAlternativeTree = (options) => {
    const buildTree = (obj) => {
      return Object.keys(obj).map((key) => {
        if (Array.isArray(obj[key])) {
          return {
            value: key,
            label: key,
            children: obj[key].map((subValue) => ({
              parent: key,
              value: subValue,
              label: subValue,
            })),
          };
        } else {
          return {
            value: key,
            label: key,
            children: buildTree(obj[key]),
          };
        }
      });
    };
    return buildTree(options);
  };

  const mapIncidentAlternativeTreeRiesgo = () => {
    return Object.keys(incidentEnumerators.riesgo).reduce((acc, keyCatRiesgo) => {
      const riesgos = incidentEnumerators.riesgo[keyCatRiesgo];
      acc.push({
        children: riesgos.map((riesgoItem) => ({
          value: riesgoItem.riesgo,
          label: riesgoItem.riesgo,
          codigo: riesgoItem.codigo,
          tipo: riesgoItem.tipo,
          familia: riesgoItem.familia,
        })),
        label: keyCatRiesgo,
      });
      return acc;
    }, [] as any[]);
  };

  const tipoRespuestaIncidenteOptions = [
    // --- los tuyos (tal cual) ---
    {
      value: 'incident_incidentCode',
      label: i18n('entities.incident.fields.incidentCode') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_withLostDays',
      label: i18n('entities.incident.fields.withLostDays') + ` (${i18n('entities.incident.name')})`,
      responseType: 'checkbox',
    },
    {
      value: 'incident_initialClassification',
      label: i18n('entities.incident.fields.initialClassification') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentEnum('initialClassification', incidentEnumerators.initialClassification),
    },
    {
      value: 'incident_finalClassification',
      label: i18n('entities.incident.fields.finalClassification') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentEnum('finalClassification', incidentEnumerators.finalClassification),
    },
    {
      value: 'incident_severity',
      label: i18n('entities.incident.fields.severity') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentEnum('severity', incidentEnumerators.severity),
    },
    {
      value: 'incident_label',
      label: i18n('entities.incident.fields.label') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_incidentDate',
      label: i18n('entities.incident.fields.incidentDate') + ` (${i18n('entities.incident.name')})`,
      responseType: 'date',
    },
    {
      value: 'incident_incidentDateTime',
      label: i18n('entities.incident.fields.incidentDateTime') + ` (${i18n('entities.incident.name')})`,
      responseType: 'time',
    },
    {
      value: 'incident_dateFrom',
      label: i18n('entities.incident.fields.dateFrom') + ` (${i18n('entities.incident.name')})`,
      responseType: 'date',
    },
    {
      value: 'incident_dateFromTime',
      label: i18n('entities.incident.fields.dateFromTime') + ` (${i18n('entities.incident.name')})`,
      responseType: 'time',
    },
    {
      value: 'incident_dateTo',
      label: i18n('entities.incident.fields.dateTo') + ` (${i18n('entities.incident.name')})`,
      responseType: 'date',
    },
    {
      value: 'incident_responsibleAdvisor',
      label: i18n('entities.incident.fields.responsibleAdvisor') + ` (${i18n('entities.incident.name')})`,
      responseType: 'perfilEmpleado',
    },
    {
      value: 'incident_areaManager',
      label: i18n('entities.incident.fields.areaManager') + ` (${i18n('entities.incident.name')})`,
      responseType: 'usuario',
    },
    {
      value: 'incident_incidentLocation',
      label: i18n('entities.incident.fields.incidentLocation') + ` (${i18n('entities.incident.name')})`,
      responseType: 'location',
    },
    {
      value: 'incident_incidentDescription',
      label: i18n('entities.incident.fields.incidentDescription') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_narrativeAccount',
      label: i18n('entities.incident.fields.narrativeAccount') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_incidentStatus',
      label: i18n('entities.incident.fields.incidentStatus') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentEnum('incidentStatus', incidentEnumerators.incidentStatus),
    },
    {
      value: 'incident_company',
      label: i18n('entities.incident.fields.company') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_employee',
      label: i18n('entities.incident.fields.employee') + ` (${i18n('entities.incident.name')})`,
      responseType: 'perfilEmpleado',
    },
    // --- faltantes según schema/envíos anteriores ---
    {
      value: 'incident_daysAdjustment',
      label: i18n('entities.incident.fields.daysAdjustment') + ` (${i18n('entities.incident.name')})`,
      responseType: 'numberField',
    },
    {
      value: 'incident_entryType',
      label: i18n('entities.incident.fields.entryType') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentEnum('entryType', incidentEnumerators.entryType),
    },
    // riesgos (strings)
    {
      value: 'incident_riesgo',
      label: i18n('entities.incident.fields.riesgo') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentAlternativeTreeRiesgo(),
      alternativeTree: true,
    },
    // enumerators
    // {
    //   value: 'incident_origin',
    //   label: i18n('entities.incident.fields.origin') + ` (${i18n('entities.incident.name')})`,
    //   responseType: 'multipleChoice',
    //   subOptions: mapIncidentEnum('origin', incidentEnumerators.origin.filter(Boolean)),
    // },
    {
      value: 'incident_contractType',
      label: i18n('entities.incident.fields.contractType') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapPerfilEmpleadoEnum('contractType', perfilEmpleadoEnumerators.contractType),
    },
    // narrativa extra
    {
      value: 'incident_narrativeWhere',
      label: i18n('entities.incident.fields.narrativeWhere') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_narrativeHow',
      label: i18n('entities.incident.fields.narrativeHow') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_narrativeWhen',
      label: i18n('entities.incident.fields.narrativeWhen') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    // relaciones varias
    {
      value: 'incident_contractorCompany',
      label: i18n('entities.incident.fields.contractorCompany') + ` (${i18n('entities.incident.name')})`,
      responseType: 'thirdParty',
    },
    {
      value: 'incident_management',
      label: i18n('entities.incident.fields.management') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    // bloque nuevo
    {
      value: 'incident_submissionDate',
      label: i18n('entities.incident.fields.submissionDate') + ` (${i18n('entities.incident.name')})`,
      responseType: 'date',
    },
    {
      value: 'incident_reportDate',
      label: i18n('entities.incident.fields.reportDate') + ` (${i18n('entities.incident.name')})`,
      responseType: 'date',
    },
    // IncidentFormNew: horarios, trabajador, organización, riesgo (campos visibles)
    {
      value: 'incident_horaIngresoTrabajo',
      label: i18n('entities.incident.fields.horaIngresoTrabajo') + ` (${i18n('entities.incident.name')})`,
      responseType: 'time',
    },
    {
      value: 'incident_horaSalidaTrabajo',
      label: i18n('entities.incident.fields.horaSalidaTrabajo') + ` (${i18n('entities.incident.name')})`,
      responseType: 'time',
    },
    {
      value: 'incident_fullName',
      label: i18n('entities.incident.fields.fullName') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_registrationNumber',
      label: i18n('entities.incident.fields.registrationNumber') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_email',
      label: i18n('entities.incident.fields.email') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_jobPosition',
      label: i18n('entities.incident.fields.jobPosition') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_companyStartDate',
      label: i18n('entities.incident.fields.companyStartDate') + ` (${i18n('entities.incident.name')})`,
      responseType: 'date',
    },
    {
      value: 'incident_contractEndDate',
      label: i18n('entities.incident.fields.contractEndDate') + ` (${i18n('entities.incident.name')})`,
      responseType: 'date',
    },
    {
      value: 'incident_companyExperienceYears',
      label: i18n('entities.incident.fields.companyExperienceYears') + ` (${i18n('entities.incident.name')})`,
      responseType: 'numberField',
    },
    {
      value: 'incident_companyExperienceMonths',
      label: i18n('entities.incident.fields.companyExperienceMonths') + ` (${i18n('entities.incident.name')})`,
      responseType: 'numberField',
    },
    {
      value: 'incident_companyExperienceDays',
      label: i18n('entities.incident.fields.companyExperienceDays') + ` (${i18n('entities.incident.name')})`,
      responseType: 'numberField',
    },
    {
      value: 'incident_jobStartDate',
      label: i18n('entities.incident.fields.jobStartDate') + ` (${i18n('entities.incident.name')})`,
      responseType: 'date',
    },
    {
      value: 'incident_workExperienceYears',
      label: i18n('entities.incident.fields.workExperienceYears') + ` (${i18n('entities.incident.name')})`,
      responseType: 'numberField',
    },
    {
      value: 'incident_workExperienceMonths',
      label: i18n('entities.incident.fields.workExperienceMonths') + ` (${i18n('entities.incident.name')})`,
      responseType: 'numberField',
    },
    {
      value: 'incident_workExperienceDays',
      label: i18n('entities.incident.fields.workExperienceDays') + ` (${i18n('entities.incident.name')})`,
      responseType: 'numberField',
    },
    {
      value: 'incident_ageYears',
      label: i18n('entities.incident.fields.ageYears') + ` (${i18n('entities.incident.name')})`,
      responseType: 'numberField',
    },
    {
      value: 'incident_gender',
      label: i18n('entities.incident.fields.gender') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapPerfilEmpleadoEnum('gender', perfilEmpleadoEnumerators.gender),
    },
    {
      value: 'incident_birthDate',
      label: i18n('entities.incident.fields.birthDate') + ` (${i18n('entities.incident.name')})`,
      responseType: 'date',
    },
    {
      value: 'incident_nationality',
      label: i18n('entities.incident.fields.nationality') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapPerfilEmpleadoEnum('nationality', perfilEmpleadoEnumerators.nationality),
    },
    {
      value: 'incident_injuredZoneSpecific',
      label: i18n('entities.incident.fields.injuredZoneSpecific') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentAlternativeTree(incidentEnumerators.zonaAfectada),
      alternativeTree: true,
    },
    {
      value: 'incident_locations', // centro de trabajo
      label: i18n('entities.incident.fields.locations') + ` (${i18n('entities.incident.name')})`,
      responseType: 'location',
      isMultiple: true,
    },
    {
      value: 'incident_age',
      label: i18n('entities.incident.fields.age') + ` (${i18n('entities.incident.name')})`,
      responseType: 'numberField',
    },
    {
      value: 'incident_workExperience',
      label: i18n('entities.incident.fields.workExperience') + ` (${i18n('entities.incident.name')})`,
      responseType: 'numberField',
    },
    // arrays de strings
    {
      value: 'incident_causaInmediata1',
      label: i18n('entities.incident.fields.causaInmediata1') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentAlternativeTreePath(incidentEnumerators.causasInmediatas),
      alternativeTree: true,
    },
    {
      value: 'incident_causaInmediata2',
      label: i18n('entities.incident.fields.causaInmediata2') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentAlternativeTreePath(incidentEnumerators.causasInmediatas),
      alternativeTree: true,
    },
    {
      value: 'incident_causaInmediata3',
      label: i18n('entities.incident.fields.causaInmediata3') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentAlternativeTreePath(incidentEnumerators.causasInmediatas),
      alternativeTree: true,
    },
    {
      value: 'incident_causaBasica1',
      label: i18n('entities.incident.fields.causaBasica1') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentAlternativeTreePath(incidentEnumerators.causasBasicas),
      alternativeTree: true,
    },
    {
      value: 'incident_causaBasica2',
      label: i18n('entities.incident.fields.causaBasica2') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentAlternativeTreePath(incidentEnumerators.causasBasicas),
      alternativeTree: true,
    },
    {
      value: 'incident_causaBasica3',
      label: i18n('entities.incident.fields.causaBasica3') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentAlternativeTreePath(incidentEnumerators.causasBasicas),
      alternativeTree: true,
    },
    {
      value: 'incident_medidasDeControl',
      label: i18n('entities.incident.fields.medidasDeControl') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
      isMultiple: true,
    },
    {
      value: 'incident_medidas_control_medida',
      label: `Medida (tabla medidas de control) (${i18n('entities.incident.name')})`,
      responseType: 'textField',
      isMultiple: true,
    },
    {
      value: 'incident_medidas_control_responsable',
      label: `Responsable (tabla medidas de control) (${i18n('entities.incident.name')})`,
      responseType: 'textField',
      isMultiple: true,
    },
    {
      value: 'incident_testigos',
      label: i18n('entities.incident.fields.testigos') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
      isMultiple: true,
    },
    {
      value: 'incident_contactPhone',
      label: i18n('entities.incident.fields.contactPhone') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_directManagerPosition',
      label: i18n('entities.incident.fields.directManagerPosition') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_propertyDamage',
      label: i18n('entities.incident.fields.propertyDamage') + ` (${i18n('entities.incident.name')})`,
      responseType: 'yesNo',
    },
    {
      value: 'incident_workPerformed',
      label: i18n('entities.incident.fields.workPerformed') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_atSea',
      label: i18n('entities.incident.fields.atSea') + ` (${i18n('entities.incident.name')})`,
      responseType: 'yesNo',
    },
    {
      value: 'incident_causingAgent',
      label: i18n('entities.incident.fields.causingAgent') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_injuryType',
      label: i18n('entities.incident.fields.injuryType') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentEnum('injuryType', incidentEnumerators.injuryType),
    },
    {
      value: 'incident_narratorName',
      label: i18n('entities.incident.fields.narratorName') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_narratorRole',
      label: i18n('entities.incident.fields.narratorRole') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_causalEffect',
      label: i18n('entities.incident.fields.causalEffect') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_causalProblem',
      label: i18n('entities.incident.fields.causalProblem') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_causalCause1',
      label: i18n('entities.incident.fields.causalCause1') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_causalCause2',
      label: i18n('entities.incident.fields.causalCause2') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_causalCause3',
      label: i18n('entities.incident.fields.causalCause3') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_investigationConclusion',
      label: i18n('entities.incident.fields.investigationConclusion') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_incidentWeekday',
      label: i18n('entities.incident.fields.incidentWeekday') + ` (${i18n('entities.incident.name')})`,
      responseType: 'day',
    },
    {
      value: 'incident_accionesSubestandar',
      label: i18n('entities.incident.fields.accionesSubestandar') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multiCheckbox',
      allowOthersOption: true,
      subOptions: incidentEnumerators.accionesSubestandar.map((option) => ({id: option, label: option})),
    },
    {
      value: 'incident_condicionesSubestandar',
      label: i18n('entities.incident.fields.condicionesSubestandar') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multiCheckbox',
      allowOthersOption: true,
      subOptions: incidentEnumerators.condicionesSubestandar.map((option) => ({id: option, label: option})),
    },
    {
      value: 'incident_tipoAccidente',
      label: i18n('entities.incident.fields.tipoAccidente') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multiCheckbox',
      allowOthersOption: true,
      subOptions: incidentEnumerators.tipoAccidente.map((option) => ({id: option, label: option})),
    },
    // archivos
    {
      value: 'incident_testigosImages',
      label: i18n('entities.incident.fields.testigosImages') + ` (${i18n('entities.incident.name')})`,
      responseType: 'files',
    },
    {
      value: 'incident_images',
      label: i18n('entities.incident.fields.images') + ` (${i18n('entities.incident.name')})`,
      responseType: 'files',
    },
  ];

  const fakeResponseTypeToNormal = (formato: any[] = []) => {
    formato.forEach((seccion: any) => {
      seccion.subseccions.forEach((subseccion: any) => {
        subseccion.preguntas.forEach((pregunta: any) => {
          const tipoRespuestaIncidente = tipoRespuestaIncidenteOptions.find(
            (tipoRespuestaIncidente) => tipoRespuestaIncidente.value === pregunta.tipoRespuesta,
          );
          if (tipoRespuestaIncidente) {
            pregunta.tipoRespuesta = tipoRespuestaIncidente.responseType;
            const wire = tipoRespuestaIncidente.value;
            pregunta.keyPointer = wire.startsWith("incident_")
              ? wire.slice("incident_".length)
              : wire;
          }
        });
      });
    });
    return formato;
  };

  const onSubmit = (values) => {
    const formato = values['formato'] || [];
    if (values['clasificacion'] === 'incidente' || values['clasificacion'] === 'investigacionIncidente') {
      values['formato'] = fakeResponseTypeToNormal(formato);
    }
    values.conditionalRules = sanitizeConditionalRules(values.conditionalRules, values.formato);
    const payload = {...values};
    applyDivisionIdPolicyToPayload(payload, tenantAccessContext, {
      treatAsTransversalSubmit: isTenantAdmin,
    });
    props.onSubmit(props.record?.id, payload);
  };

  const onReset = () => {
    Object.keys(initialValues).forEach((key) => {
      form.setValue(key, initialValues[key]);
    });
  };

  const {saveLoading, modal} = props;

  const [isOpenExitConfirmModal, setIsOpenExitConfirmModal] = useState(false);

  const doOpenExitConfirmModal = () => {
    setIsOpenExitConfirmModal(true);
  };

  const doCloseExitConfirmModal = () => {
    setIsOpenExitConfirmModal(false);
  };

  const doExit = () => {
    doCloseExitConfirmModal();
    props.onCancel();
  };
  //const watchClasificacion = useWatch({ name: 'clasificacion', control: form.control });
  //const watchFormato = useWatch({ name: 'formato', control: form.control }); // esto explota, solo descomentar en debug

  const clasificacionValue = useWatch({name: 'clasificacion', control: form.control, defaultValue: 'operativa'});
  //let formatoValue = form.getValues('formato');

  const user = useSelector(authSelectors.selectCurrentUser);
  const userName = user?.fullName || user?.email;

  const saveDataTarea = (id, data) => {
    console.log('%c⧭ saveDataTarea', 'color: #735656', {data});
    form.setValue('tareaData', data);
    setValid(true);
  };
  const editDataTarea = () => {
    form.setValue('tareaData', {});
    setValid(false);
  };

  const formValues = form.getValues();
  //console.log('%c⧭', 'color: #408059', {formValues});

  // const [expanded, setExpanded] = React.useState<string | false>(false); //fields?.[0]?.id);

  // const handleChange = (panel: string) => (event: React.ChangeEvent<{}>, newExpanded: boolean) => {
  //   setExpanded(newExpanded ? panel : false);
  // };

  const [loadingIA, setLoadingIA] = useState(false);

  const wireTaskConfigToDomain = (tc: any) => {
    if (!tc) return tc;
    return {
      ...tc,
      fields: (tc.fields || []).map((f: any) => ({
        ...f,
        responseType: wireAnswerTypeToDomain(String(f.responseType ?? f.tipoRespuesta ?? 'text')),
      })),
    };
  };

  const applyLocalFormatSnapshot = (snap: any) => {
    if (Array.isArray(snap)) {
      form.setValue('formato', snap);
      return;
    }
    if (snap?.formato) form.setValue('formato', snap.formato);
    if (snap?.conditionalRules !== undefined) {
      form.setValue(
        'conditionalRules',
        sanitizeConditionalRules(snap.conditionalRules || [], snap?.formato ?? form.getValues('formato')),
      );
    }
  };

  const validAnswerTypes = getWireAnswerTypesForClasificacion(clasificacionValue).filter(
    (answerType) => showBlumarIncidentFields || !BLUMAR_INCIDENT_ANSWER_TYPES.has(answerType),
  );

  const systemExample = {
    sections: [
      {
        name: 'Seguridad',
        sectionIndex: 0,
        subsections: [
          {
            name: 'Inspección',
            subsectionIndex: 0,
            questions: [
              {
                name: 'fechaInspeccion',
                label: 'Fecha de Inspección',
                answerType: 'date',
                required: true,
              },
              {
                name: 'areaInspeccionada',
                label: 'Área Inspeccionada',
                answerType: 'text',
                required: true,
              },
              {
                name: 'condicionEquipo',
                label: 'Condición del Equipo',
                answerType: 'inspectionCheck',
                required: true,
              },
              {
                name: 'observaciones',
                label: 'Observaciones',
                answerType: 'text',
              },
            ],
          },
        ],
      },
    ],
    systemMessage: `¡Claro! Vamos a convertir este formulario en una estructura digital utilizando el formato Planiwise.

Análisis del formulario:

    (Escribe un análisis del formulario, si es necesario).

Estructura propuesta en Planiwise:

1. Sección de Control de Proceso:

    (Acá especificar las preguntas generales de la sección no dinámica, por ejemplo: Fecha, Hora, Área, etc.)

2. Subsección de Enfriado (Tabla Dinámica):

    Subcategoría (Dinámica): "Registro de Enfriado" (se replicará para cada registro horario).
    Columnas (Preguntas):
        (Acá debes especificar las preguntas de la tabla, por ejemplo: Hora, Temperatura, Observaciones, etc.)

3. Pie de formulario:

    (Espeficiar si hay un pie de formulario con observaciones finales o aclaraciones).

Consideraciones:

    (Listar las consideraciones)`,
  };
  const systemMessage = () => {
    const exampleQuestionarie = JSON.stringify(systemExample);
    const incidentAnswerTypeGuidance =
      clasificacionValue === 'incidente' || clasificacionValue === 'investigacionIncidente'
        ? `
Incidente (solo esta clasificación): Para respuestas que deban volcarse al registro general del incidente, usa como 'answerType' exactamente uno de los valores 'incident_<campo>' listados arriba (no inventes sufijos). Para el resto del formulario (operativo, tablas, etc.) usa tipos normales sin prefijo 'incident_' (p. ej. 'text', 'date', 'selection').
Testigos: no uses un solo campo JSON; en una subsección dinámica (isDynamic true) elige "Testigos (incidente)" en el select Tabla de subsección (incidente) (\`subTableKind\` = "testigos"; \`subTable\` abrevia subsection table; el título visible sigue en categoria). Configura columnas en \`subTableConfig.fields\` con \`columnId\` name|role|contact|statement (inglés, estable) y keyPointers incident_testigo_* (statement: text + havePhotos para additionalPhotos).
${
  showBlumarIncidentFields
    ? 'Entrevistas: en una subsección dinámica elige "Entrevistas (incidente)" (`subTableKind` = "entrevistas"). Columnas role|name|experience|workPerformed y keyPointers incident_entrevista_rol, incident_entrevista_nombre, incident_entrevista_experiencia, incident_entrevista_trabajo.'
    : ''
}
Checklist otro tipo de tabla de subsección (incidente) (código): (1) Tres copias del mismo archivo (misma lógica y literales \`subTableKind\`): \`stem-web-backend/src/incidentSubTable.ts\`, \`planiwise-frontend/src/modules/incident/incidentSubTable.ts\`, \`planiwise-app/functions/incidentSubTable.ts\` — cualquier edición debe aplicarse en las tres antes de desplegar. Añade un elemento a \`INCIDENT_SUBTABLE_DEFS\` (subTableKind, editorLabel, incidentRowsPath, columnTemplates con keyPointer + rowField + supportsPhotos/rowPhotosField; \`rowKeyPointers\` = unión de keyPointer de plantillas; opcional \`incidentPdfSummaryFieldKey\` si el PDF usa un \`fieldDef.key\` fijo para ese resumen; opcional aggregateRowsFromLista / applyIncidentRowToProcedureQuestion). No uses otro módulo \`incidentSubTableConstants\`. (2) Modelo/schema incident: array u objeto en la ruta incidentRowsPath. (3) incidentSubTableProcedureList (pareja frontend + app, dos repos): prefill itera defs; mantener ambos archivos alineados. (4) UI modelo: \`listIncidentSubTableDefs\`; editor de columnas por subTableKind según hasColumnConfigEditor. (5) keyPointers únicos por tabla; \`allRegisteredIncidentSubTableRowKeyPointers\` al registrar.
`
        : '';
    return {
      role: 'system',
      content: [
        {
          type: 'text',
          text: `Eres un asistente creado y desarrollado por una empresa de software web llamada Planiwise, usando tecnología de OpenAI. 
Estás ayudando a generar formularios en español y en formato JSON. Deben una estructura similar al ejemplo que te dejaré más abajo. Nunca dejes secciones o atributos vacíos.

'answerType' debe ser exactamente uno de estos: ${validAnswerTypes.join(', ')}. No se permiten otros tipos de respuesta.
'selection' y 'checkBoxList' requieren un array 'options' (lista plana de strings, o árbol si alternativeTree es true).
'text' es texto libre; 'number' es numérico; 'ammount' es dinero.
'inspectionCheck' permite Aprobado/Observado/Rechazado.
'rut' es identificación tipo RUT (persona).
'barcode' es códigos de barra.
'date', 'time', 'day', 'month' y 'year' son fechas y horas.
'asset' es activos (equipos, máquinas o motorizados).
'location' es ubicaciones.
'thirdParty' es terceros.
'project' es proyectos.
'perfilEmpleado' es colaboradores (RRHH).
'usuario' es usuarios.
'asignadorFuncion' es Teams (grupos de usuarios).
'files' es adjuntos.
'meterReading' es lecturas de medidor.
'textFromImage' es texto desde imagen (IA).
'signature' es firma.
${incidentAnswerTypeGuidance}
Pregunta: 'required' (obligatoria), 'allowDateBefore', 'haveCriticality', 'havePhotos', 'haveFiles', 'tieneComentario', 'commentLabel', 'allowOthersOption', 'numMin'/'numMax' (número), 'amountMin'/'amountMax' (monto), 'allowMultipleAnswers', 'multipleAnswersType', 'onlyOneOption', 'defaultFirstOption', 'alternativeTree', 'alternativeTreeDisplayLevels' (1-3, solo si alternativeTree en selection; cuántos niveles finales del árbol mostrar al visualizar la respuesta), 'restrictByLocation', 'hiddenInPdf', 'tieneLink'/'link', 'tienePuntaje'/'puntaje', 'unidadMedida'.

Subsección: 'isDynamic', 'isTask' (crear tarea desde el formulario al cerrar) con 'taskConfig', 'allowNotApplicable' (permitir marcar N/A y ocultar preguntas). Sección: 'allowNotApplicable' (ocultar subsecciones). Raíz del JSON puede incluir 'conditionalRules': array de reglas con enabled, groupCombinator ('AND'|'OR' entre grupos), groups ([{ combinator: 'AND'|'OR', conditions: [{ questionKey, matchMode, matchText }] }]), actions (SIGNATURE|TASK|EMAIL|NOTIFICATION), recipients, validatorRecipients (mismos modos que recipients salvo EMAIL; validadores de la tarea gatillada), modeloTarea, taskDueDays, emailHighlightQuestionKeys, attachTaskPdf (boolean, default true: adjuntar el PDF de la tarea al correo cuando la acción es EMAIL), emailSeparateRecipients (boolean, default false: un correo con todos en CC; true = un correo por destinatario). questionKey vacío = cualquier respuesta. matchMode: CONTAINS, EQUALS, NOT_EQUALS, STARTS_WITH, ENDS_WITH, REGEX, IS_EMPTY, IS_NOT_EMPTY, GREATER_THAN, LESS_THAN, GREATER_OR_EQUAL, LESS_OR_EQUAL.

Los nombres de los campos (name) deben ser únicos dentro del formulario.

'isDynamic' en subsections permite repetir un bloque de preguntas (p. ej. columnas de tabla).

'systemMessage' es para responder al usuario sobre su solicitud de generación del formulario.

¿Te parece si primero analizas bien el contenido del formulario como en el ejemplo y después generas el JSON?

JSON DE EJEMPLO PARA QUE ENTIENDAS LA ESTRUCTURA: 
"${exampleQuestionarie}"
`,
        },
      ],
    };
  };

  const [messages, setMessages] = useState<any>([
    {
      role: 'assistant',
      content: `¡Hola! Soy tu asistente para la creación de procedimientos. Te ayudaré a crearlos en base a lo que me pidas. Un ejemplo de solicitud es: "Quiero un formulario para registrar incidentes".`,
    },
  ]);
  const [formatHistory, setFormatHistory] = useState<any>([]);
  const [historyIndex, setHistoryIndex] = useState<any>(0);

  const messageRef = useRef<any>();
  //const html = (strings, ...values) => String.raw({raw: strings}, ...values);

  const handleSendMessage = async (event) => {
    event.preventDefault();

    if (loadingIA) return;

    // get message from input
    const message = messageRef.current.value;
    if (!message) return;

    // get current format
    const currentFormat = form.getValues('formato');
    const openAIFormat = convertToOpenAIFormat(currentFormat);

    // add user message
    const messagesToSend = [
      {
        role: 'user',
        content: [
          {
            type: 'text',
            text: `Formulario actual: ${JSON.stringify(openAIFormat)}.\nMi solicitud es: "${message}" (Responder en json)`,
          },
        ],
      },
    ];
    const fotosAdjuntas = form.getValues('fotosAdjuntas');
    let imageUrl: any = null;
    if (fotosAdjuntas && fotosAdjuntas.length > 0) {
      imageUrl = await urlToBase64(fotosAdjuntas[0].downloadUrl);
      messagesToSend[0].content.push({
        type: 'image_url',
        image_url: {
          url: imageUrl,
        },
      } as any);
    }

    // find last assistant message
    const lastAssistantMessage = messages.findLast((message) => message.role === 'assistant');
    if (lastAssistantMessage) {
      // add last assistant message to new messages
      messagesToSend.unshift(lastAssistantMessage);
    }

    const userMessage = {role: 'user', content: message};

    // set messages to show
    setMessages([
      userMessage,
      {
        role: 'assistant',
        content: `Procesando solicitud...`,
      },
    ]);

    // clear input
    messageRef.current.value = '';

    // request chat completion
    messagesToSend.unshift(systemMessage());

    requestChatCompletion({messagesToSend, userMessage, openAIFormat});
  };

  const urlToBase64 = async (url) => {
    try {
      const response = await axios.get(url + '?v=' + (Math.floor(Math.random() * 10) + 1), {responseType: 'blob'});
      const resizedImg = await resizeFile(response.data);
      return resizedImg;
    } catch (error) {
      console.log('urlToBase64 error', {error});
      return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNgYAAAAAMAASsJTYQAAAAASUVORK5CYII=';
    }
  };
  const resizeFile = (file) => {
    console.log('%c⧭', 'color: #86bf60', 'RESIZING!');
    return new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        2048,
        2048,
        'JPEG',
        100,
        0,
        (uri) => {
          resolve(uri);
        },
        'base64',
      );
    });
  };

  const regenerateCompletion = () => {
    const openAIFormat = formatHistory[historyIndex].openAIFormat;
    const messagesToSend = formatHistory[historyIndex].messagesToSend;
    const userMessage = formatHistory[historyIndex].userMessage;
    requestChatCompletion({messagesToSend, userMessage, openAIFormat});
  };

  const requestChatCompletion = async ({messagesToSend, userMessage, openAIFormat}) => {
    if (loadingIA) return;
    setLoadingIA(true);
    let response;
    try {
      // get result from openAI
      response = await OpenAIService.chatCompletion(messagesToSend);
    } catch (e) {
      console.log(e);
      Message.error('Error al conectar con servicio de IA');
    }
    let messagesToShow = [userMessage];
    if (response?.content) {
      // parse response
      const parsed = JSON.parse(response.content);

      // add system message to new messages
      messagesToShow.push({
        role: 'assistant',
        content: parsed?.systemMessage || '',
      });
      // convert to local format
      const localFormat = convertToLocalFormat(parsed);
      console.log({response, parsed, localFormat});

      // save new format in history
      pushHistory({localFormat, messagesToShow, messagesToSend, userMessage, openAIFormat});

      // set new format
      if (localFormat?.formato) form.setValue('formato', localFormat.formato);
      if (localFormat?.conditionalRules !== undefined) {
        form.setValue(
          'conditionalRules',
          sanitizeConditionalRules(localFormat.conditionalRules, localFormat.formato ?? form.getValues('formato')),
        );
      }
    } else {
      // (no content in response) TODO: better handle for errors
      // find last assistant message
      const lastAssistantMessage = messages.findLast((message) => message.role === 'assistant');
      if (lastAssistantMessage) {
        // set error message
        lastAssistantMessage.content = 'Error al conectar con servicio de IA';
        messagesToShow.push(lastAssistantMessage);
      }
      // save message in history
      pushHistory({
        localFormat: {formato: form.getValues('formato'), conditionalRules: form.getValues('conditionalRules')},
        messagesToShow,
        messagesToSend,
        userMessage,
        openAIFormat,
      });
    }
    // set messages
    setMessages(messagesToShow);
    setLoadingIA(false);
  };

  const pushHistory = ({
    localFormat = [],
    messagesToShow = [],
    messagesToSend = [],
    userMessage = null,
    openAIFormat = [],
  }: any) => {
    const newHistory = {
      localFormat,
      messagesToShow,
      messagesToSend,
      userMessage,
      openAIFormat,
    };

    let newFormatHistory = [...formatHistory];

    // if historyIndex is not at the end, remove all history after it
    if (historyIndex < formatHistory.length - 1) {
      newFormatHistory = newFormatHistory.slice(0, historyIndex + 1);
    }

    // push new history
    newFormatHistory.push(newHistory);

    // set new history
    setFormatHistory(newFormatHistory);
    setHistoryIndex(newFormatHistory.length - 1);
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleUndo = async () => {
    if (historyIndex > 0) {
      // set history index
      let newHistoryIndex = historyIndex - 1;
      setHistoryIndex(newHistoryIndex);

      // get history at index
      const history = formatHistory[newHistoryIndex];

      // loading for rerendering purposes
      setLoadingIA(true);
      await sleep(500);

      // set format and messages
      applyLocalFormatSnapshot(history.localFormat);
      setMessages(history.messagesToShow);

      setLoadingIA(false);
    }
  };

  const handleRedo = async () => {
    if (historyIndex < formatHistory.length - 1) {
      // set history index
      let newHistoryIndex = historyIndex + 1;
      setHistoryIndex(newHistoryIndex);

      // get history at index
      const history = formatHistory[newHistoryIndex];

      // loading for rerendering purposes
      setLoadingIA(true);
      await sleep(500);

      // set format and messages
      applyLocalFormatSnapshot(history.localFormat);
      setMessages(history.messagesToShow);

      setLoadingIA(false);
    }
  };

  const convertToOpenAIFormat = (formato) => {
    const clasificacion = clasificacionValue;
    const sections = formato.map((seccion, sectionIndex) => {
      return {
        name: seccion.categoria,
        sectionIndex,
        allowNotApplicable: Boolean(seccion.allowNotApplicable),
        subsections: seccion.subseccions.map((subseccion, subsectionIndex) => {
          const sub: any = {
            name: subseccion.categoria,
            subsectionIndex,
            isDynamic: subseccion.isDynamic,
            isTask: Boolean(subseccion.isTask),
            allowNotApplicable: Boolean(subseccion.allowNotApplicable),
          };
          if (subseccion.subTableKind) {
            sub.subTableKind = subseccion.subTableKind;
          }
          if (subseccion.isTask && subseccion.taskConfig) {
            sub.taskConfig = {
              ...subseccion.taskConfig,
              fields: (subseccion.taskConfig.fields || []).map((f: any) => ({
                ...f,
                responseType: domainAnswerTypeToWire(f.responseType),
              })),
            };
          }
          if (subseccion.isTask) {
            sub.questions = [];
            return sub;
          }
          sub.questions = (subseccion.preguntas || []).map((pregunta, questionIndex) => {
            const pt = pregunta.tipoRespuesta;
            const baseFields = pickDomainQuestionFieldsForWire(pregunta);
            const titulo = pregunta.pregunta?.titulo ?? pregunta.pregunta?.label ?? '';
            if (pt === 'multipleChoice') {
              return {
                name: titulo,
                questionIndex,
                answerType: 'selection',
                options: domainOptionsToWire(pregunta.alternativas, undefined, 'multipleChoice', pregunta.alternativeTree) ?? [],
                ...baseFields,
              };
            }
            if (pt === 'multiCheckbox') {
              return {
                name: titulo,
                questionIndex,
                answerType: 'checkBoxList',
                options:
                  domainOptionsToWire(undefined, pregunta.alternativasCheckbox, 'multiCheckbox', pregunta.alternativeTree) ?? [],
                ...baseFields,
              };
            }
            const answerType = domainToWireAnswerTypeForExport(pt, pregunta.keyPointer, clasificacion);
            const out: any = {
              name: titulo,
              questionIndex,
              answerType,
              ...baseFields,
            };
            const hasAlts =
              (pregunta.alternativas && pregunta.alternativas.length) ||
              (pregunta.alternativasCheckbox && pregunta.alternativasCheckbox.length) ||
              pregunta.alternativeTree;
            const incidentDefExport = pt?.startsWith('incident_')
              ? tipoRespuestaIncidenteOptions.find((o) => o.value === pt)
              : (clasificacion === 'incidente' || clasificacion === 'investigacionIncidente') && pregunta.keyPointer
                ? tipoRespuestaIncidenteOptions.find((o) => o.value === `incident_${pregunta.keyPointer}`)
                : null;
            if (incidentDefExport && hasAlts) {
              const mode =
                pregunta.alternativasCheckbox && pregunta.alternativasCheckbox.length ? 'multiCheckbox' : 'multipleChoice';
              out.options = domainOptionsToWire(
                pregunta.alternativas,
                pregunta.alternativasCheckbox,
                mode,
                pregunta.alternativeTree,
              );
            }
            return out;
          });
          return sub;
        }),
      };
    });
    const out: any = {sections};
    const cr = form.getValues('conditionalRules');
    if (Array.isArray(cr) && cr.length) {
      out.conditionalRules = cr;
    }
    return out;
  };

  const createOptionLabelId = (option, index) => ({
    label: option.label || option,
    id: `${index}-${(option.value ?? option.label ?? option).toString().toLowerCase().replace(/\W/g, '')}`,
  });

  const convertToLocalFormat = (openAIFormat) => {
    const formato = openAIFormat.sections?.map((section) => {
      return {
        categoria: section.name,
        isDynamic: false,
        allowNotApplicable: Boolean(section.allowNotApplicable),
        subseccions: section.subsections?.map((subsection) => {
          const subLocal: any = {
            categoria: subsection.name,
            isDynamic: subsection.isDynamic,
            isTask: Boolean(subsection.isTask),
            allowNotApplicable: Boolean(subsection.allowNotApplicable),
            subTableKind: subsection.subTableKind || '',
          };
          if (subsection.isTask) {
            const rawTc = wireTaskConfigToDomain(subsection.taskConfig) || {};
            const normalized = normalizeIsTaskConfig({isTask: true, taskConfig: rawTc, preguntas: []});
            subLocal.taskConfig = normalized ?? rawTc;
            subLocal.preguntas = [];
            return subLocal;
          }
          subLocal.preguntas = (subsection.questions || []).map((question) => {
            const rawAt = question.answerType;
            const wireTipo = wireAnswerTypeToDomain(rawAt);
            const incidentDef = wireTipo.startsWith('incident_')
              ? tipoRespuestaIncidenteOptions.find((o) => o.value === wireTipo)
              : null;

            const value: any = {
              pregunta: {
                titulo: question.label || question.name,
                label: question.label || question.name,
              },
              tipoRespuesta: wireTipo,
            };

            applyWireQuestionFieldsToDomain(question, value);

            // Incidente: mantener tipoRespuesta en wire (incident_<keyPointer>) como la IA; igual que en edición manual.
            // keyPointer sufijo para sincronía con PreguntaFormularioForm; la normalización a domino va en fakeResponseTypeToNormal al guardar.
            if (wireTipo.startsWith('incident_')) {
              value.keyPointer = incidentKeyPointerFromTipo(wireTipo);
              if (!incidentDef) {
                console.warn('[modeloFormulario IA] incident sin match en catálogo:', wireTipo);
              }
            }

            const isMc =
              rawAt === 'selection' ||
              (wireTipo === 'multipleChoice' && !incidentDef) ||
              incidentDef?.responseType === 'multipleChoice';
            const isMcc =
              rawAt === 'checkBoxList' ||
              (wireTipo === 'multiCheckbox' && !incidentDef) ||
              incidentDef?.responseType === 'multiCheckbox';

            if (isMc && question.options != null) {
              if (!wireTipo.startsWith('incident_')) {
                value.tipoRespuesta = 'multipleChoice';
              }
              Object.assign(
                value,
                wireOptionsToAlternativas(question.options, question.alternativeTree, 'selection', createOptionLabelId),
              );
            } else if (isMcc && question.options != null) {
              if (!wireTipo.startsWith('incident_')) {
                value.tipoRespuesta = 'multiCheckbox';
              }
              Object.assign(
                value,
                wireOptionsToAlternativas(question.options, question.alternativeTree, 'checkBoxList', createOptionLabelId),
              );
            }

            if (!question.alternativeTree) {
              value.alternativeTree = false;
            }
            return value;
          });
          return subLocal;
        }),
      };
    });
    const result: any = {formato};
    if (openAIFormat.conditionalRules != null) {
      result.conditionalRules = sanitizeConditionalRules(
        openAIFormat.conditionalRules,
        result.formato,
      );
    }
    return result;
  };

  const chatContainerRef = useRef<any>(null);

  useEffect(() => {
    const chatContainer = chatContainerRef?.current;
    if (!chatContainer) return;
    setTimeout(() => {
      chatContainer.scrollTo({
        top: window.innerHeight - 200,
        behavior: 'smooth',
      });
    }, 300);
  }, [messages]);

  return (
    <FormWrapper>
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            marginBottom: '18px',
            //maxWidth: 'min-content',
          }}>
          <Box>
            <Grid container spacing={2} style={{paddingLeft: '10px'}} alignItems="center">
              <Grid item lg={6} md={6} sm={6} xs={11}>
                <InputFormItem
                  shrinkLabel={false}
                  name="nombre"
                  label={i18n('entities.modeloFormulario.fields.nombre')}
                  required={true}
                  autoFocus
                />
              </Grid>
              {/*
                <Grid item lg={12} md={8} sm={12} xs={12}>
                  <SucursalAutocompleteFormItem
                    name="sucursales"
                    label={i18n('entities.modeloFormulario.fields.sucursales')}
                    required={true}
                    showCreate={!props.modal}
                    mode="multiple"
                  />
                </Grid>
              */}

              <Grid item lg={5} md={5} sm={5} xs={11}>
                <CategoriaAutocompleteFormItem
                  name="categoriaGeneral"
                  label={i18n('entities.modeloFormulario.fields.categoriaGeneral')}
                  required={false}
                  showCreate={!props.modal}
                />
              </Grid>

              {/* <Grid item lg={12} md={8} sm={12} xs={12}>
                <HabilidadAutocompleteFormItem
                  name="habilidades"
                  label={i18n('entities.modeloFormulario.fields.habilidades')}
                  required={true}
                  showCreate={!props.modal}
                  mode="multiple"
                />
              </Grid> */}

              <Grid item lg={3} md={6} sm={12} xs={12}>
                <SelectFormItem
                  name="clasificacion"
                  label={i18n('entities.modeloFormulario.fields.clasificacion')}
                  options={modeloFormularioEnumerators.clasificacion.map((value) => ({
                    value,
                    label: i18n(`entities.modeloFormulario.enumerators.clasificacion.${value}`),
                  }))}
                  required={true}
                  defaultValue={props.record?.clasificacion || 'operativa'}
                />
              </Grid>
              <Grid item lg={3} md={3} sm={3} xs={12}>
                <Box p={2}>
                  <CheckboxFormItem
                    name="adjuntarFirmaEjecutor"
                    label={i18n('common.adjuntarFirmaEjecutor')}
                    required={false}
                    defaultValue={props.record?.adjuntarFirmaEjecutor}
                  />
                </Box>
              </Grid>

              {showDivisionIdsField && (
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <DivisionAutocompleteFormItem
                    name="divisionId"
                    label={i18n('entities.division.label')}
                    required={false}
                    showCreate={false}
                  />
                </Grid>
              )}

              <Grid item lg={6} md={6} sm={6} xs={12} style={{minHeight: 110, paddingRight: '16px'}}>
                {
                  <SwitchFormItem
                    label="Crear Tarea"
                    name="crearTarea"
                    onChange={(value) => {
                      !value && editDataTarea();
                    }}
                  />
                }
                {formValues.crearTarea && (
                  <ModeloTareaForm
                    defaultValues={{
                      titulo: formValues.nombre,
                      tipoProcedimiento: 'Evaluacion',
                      tipoVisualizacion: 'Procedimiento',
                      prioridad: 'Alta',
                    }}
                    simpleView={true}
                    saveLoading={saveLoading}
                    isEditing={false}
                    hideFormWhenReady={true}
                    onSubmit={saveDataTarea}
                    onCancel={editDataTarea}
                    disabled={valid}
                  />
                )}
              </Grid>
            </Grid>
            {hasAccessToIA && (
              <Grid item lg={7} md={7} sm={7} xs={12} style={{padding: 10, marginBottom: 48}}>
                <Grid
                  container
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: 5,
                    padding: 10,
                    //backgroundColor: '#fffbe6',
                  }}>
                  <Grid
                    item
                    xs={12}
                    container
                    style={{
                      alignItems: 'center',
                      padding: 16,
                      paddingRight: 0,
                    }}>
                    <Grid item xs>
                      <TextField
                        multiline
                        rows={0}
                        variant="outlined"
                        fullWidth
                        name="message"
                        placeholder="Escribe tu solicitud"
                        inputRef={messageRef}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            handleSendMessage(event);
                          }
                        }}
                      />{' '}
                    </Grid>
                    <Grid item>
                      <Button
                        disabled={loadingIA}
                        color="primary"
                        style={{marginLeft: 8}}
                        type="button"
                        onClick={handleSendMessage}>
                        Enviar
                      </Button>
                      {/* {loadingIA && <CircularProgress size={25} style={{marginLeft: 8}} />} */}
                    </Grid>
                    <Grid item pl={1}>
                      <ImagesFormItem
                        name="fotosAdjuntas"
                        disabled={props.disabled}
                        label={i18n('entities.modeloTarea.fields.fotosAdjuntas')}
                        required={false}
                        storage={Storage.values.modeloTareaFotosAdjuntas}
                        max={1}
                      />
                    </Grid>
                  </Grid>
                  {messages?.length > 0 && (
                    <Grid
                      item
                      xs={12}
                      style={{
                        placeContent: 'start',
                        overflowY: 'scroll',
                        maxHeight: 'calc(100vh - 200px)',
                        overflowX: 'hidden',
                        padding: 16,
                        paddingRight: 54,
                      }}>
                      {messages
                        .filter((m) => m.role !== 'system')
                        .slice(-2)
                        .map((message, index) => (
                          <div
                            key={index}
                            style={{
                              margin: '10px 0',
                              padding: '10px',
                              border: '1px solid #ddd',
                              borderRadius: '10px',
                              backgroundColor: message.role === 'user' ? 'rgb(63, 81, 181, 0.2)' : '#fffbe6',
                            }}>
                            <strong>{message.role === 'user' ? userName : 'Planiwise AI Assistant'}</strong>
                            <hr style={{opacity: 0.3}} />
                            <ReactMarkdown>{message.content}</ReactMarkdown>
                          </div>
                        ))}
                    </Grid>
                  )}
                  <Grid
                    item
                    xs={12}
                    style={{
                      padding: 6,
                    }}>
                    <div style={{display: 'flex' /*, justifyContent: 'space-between'*/}}>
                      <Button
                        color="primary"
                        style={{marginLeft: 8}}
                        type="button"
                        onClick={handleUndo}
                        disabled={loadingIA || historyIndex === 0}>
                        Deshacer
                      </Button>
                      <Button
                        color="primary"
                        style={{marginLeft: 8}}
                        type="button"
                        onClick={handleRedo}
                        disabled={loadingIA || historyIndex >= formatHistory.length - 1}>
                        Rehacer
                      </Button>
                      <Button
                        color="primary"
                        style={{marginLeft: 'auto'}}
                        type="button"
                        onClick={regenerateCompletion}
                        disabled={loadingIA || historyIndex === 0}>
                        Regenerar
                      </Button>
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <div style={{paddingLeft: 10, paddingRight: 40}}>
                {loadingIA && (
                  <div style={{textAlign: 'center'}}>
                    <CircularProgress />
                  </div>
                )}
                {!loadingIA && <ModeloPrincipalFormularioForm clasificacion={clasificacionValue} name="formato" />}

                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <div style={{paddingLeft: 10, paddingRight: 40, marginTop: 28}}>
                    <ConditionalRulesTable
                      name="conditionalRules"
                      formQuestionsPath="formato" // <- tu estructura de preguntas vive ahí
                      disabled={props.disabled}
                    />
                  </div>
                </Grid>
              </div>
            </Grid>
          </Box>
        </form>
        <FormButtons
          style={{
            flexDirection: modal ? 'Grid-reverse' : undefined,
            placeContent: 'space-between',
            maxWidth: '330px',
          }}>
          <Button
            variant="contained"
            color="primary"
            disabled={saveLoading}
            type="button"
            onClick={form.handleSubmit(onSubmit)}
            startIcon={<SaveIcon />}
            size="small">
            {i18n('common.save')}
          </Button>

          <Button disabled={saveLoading} onClick={onReset} type="button" startIcon={<UndoIcon />} size="small">
            {i18n('common.reset')}
          </Button>

          {props.onCancel ? (
            <Button disabled={saveLoading} onClick={doOpenExitConfirmModal} type="button" startIcon={<CloseIcon />} size="small">
              {i18n('common.exit')}
            </Button>
          ) : null}
        </FormButtons>
        {isOpenExitConfirmModal && (
          <ConfirmModal
            title={i18n('common.areYouSure')}
            onConfirm={doExit}
            onClose={() => doCloseExitConfirmModal()}
            okText={i18n('common.yes')}
            cancelText={i18n('common.no')}
          />
        )}
      </FormProvider>
    </FormWrapper>
  );
}

export default ModeloFormularioForm;
